import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt, formatNumber, UserSettings } from '@miq/utils';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../models/report.model';
import { LovValues, TemplateFieldsDisplayFn } from '../../../../models/template.model';
import { getAreaSqmValues, getMediaInformation, getRentFieldsData } from '../../templates-formatter.const';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../utils/dynamic-data-retrievers';
import { AVAILABILITY_TYPES } from '../common';
import { currencyLov } from '../us/us.common';
import { PACIFIC_AVAILABILITY_INDUSTRIAL_COMMON_FIELDS } from './pacific-availability.common';
import moment from 'moment';

const measurmentsNotConsider = [
  'Canopy Area',
  'Yard Area',
  'Loading Dock Area',
  'Site Area',
  'Land Area',
  'Hardstand Area',
];
const PARKING_COST_OPTIONS = {
  'NZD/PCPA': 'Year',
  'NZD/PCPM': 'Month',
  'NZD/PCPW': 'Week',
  '-': '-',
};

export const displayFnMap: TemplateFieldsDisplayFn = {
  ...PACIFIC_AVAILABILITY_INDUSTRIAL_COMMON_FIELDS(),
  538: {
    displayFn: (record: ReportRecord, options, suburb) => {
      const suburbVal = mapAndFormatDynamicValue(record, suburb);
      return options.isPPTHasSpecificFormat && suburbVal === defaultTxt ? ' ' : suburbVal;
    },
  }, // Subheader
  246: {
    displayFn: (record: ReportRecord, _options, arg) => {
      const dateAvailable = mapAndFormatDynamicValue(record, arg);
      const inFmt = UserSettings.getGlobalDateDisplayFormat('value');
      const availDate = moment(dateAvailable, arg.dataFormat || inFmt);
      if (availDate.isValid() && moment().diff(availDate, 'days') > 0) {
        return 'Immediate';
      }
      return dateAvailable;
    },
  }, // Available Date
  235: {
    displayFn: (record: ReportRecord, options, parkingCostArg, _arg2, _arg3, parkingCountArg) => {
      return getRentFieldsData(
        record,
        options,
        'property_measurement_size',
        'asking_rent_yearly',
        parkingCostArg,
        parkingCountArg
      );
    },
    isVisible: (record: ReportRecord) => {
      const availableTypeVal = digger(record, 'data.ref_availability_type_desc');
      return availableTypeVal !== AVAILABILITY_TYPES.sale;
    },
  }, // Rent (pa)
  267: {
    displayFn: (record: ReportRecord, options, parkingCostArg) => {
      const rentYearlyVal = options.isPPTHasSpecificFormat
        ? `data.property_measurement_availability/asking_rent_yearly`
        : `asking_rent_yearly`;
      record = options.isPPTHasSpecificFormat ? record : record.data[0];
      const result = digger(record, rentYearlyVal);
      if (options.isPPTHasSpecificFormat) {
        let outputValue = result.map(val => {
          if (val) {
            val = `$${formatNumber(val, undefined, '.0-2')}`;
          }
          return val;
        });
        const parkingCost = mapAndFormatDynamicValue(record, { ...parkingCostArg, uomArgument: null, uomId: null });
        if (parkingCost && parkingCost.length && parkingCost[0] != defaultTxt) {
          const parkingCostUOM = mapAndFormatDynamicValue(record, { ...parkingCostArg.uomArgument });
          const output =
            (parkingCost[0] != defaultTxt ? `$${formatNumber(parkingCost, undefined, '.0-2')}` : defaultTxt) +
            '/' +
            PARKING_COST_OPTIONS[(parkingCostUOM as string) || '-'];
          outputValue = [...outputValue, output];
        }
        return outputValue;
      }
      return result != null && result != defaultTxt ? `$${formatNumber(result, undefined, '.0-2')}` : result;
    },
    isVisible: (record: ReportRecord) => {
      const availableTypeVal = digger(record, 'data.ref_availability_type_desc');
      return availableTypeVal !== AVAILABILITY_TYPES.sale;
    },
  }, // Rent ($/sqm)
  243: {
    displayFn: (record: ReportRecord, options, areaType) => {
      if (!options.isPPTHasSpecificFormat) {
        areaType = {
          ...areaType,
          field: !!areaType.field ? areaType.field.split('/')[1] : '',
          isRecordTableData: true,
        };
        record = record.data[0];
      }
      let areaTypeVal = mapAndFormatDynamicValue(record, areaType);
      if (Array.isArray(areaTypeVal)) {
        areaTypeVal = areaTypeVal.map(area => {
          area = !measurmentsNotConsider.some(mea => mea === area) ? `${area}*` : area;
          area = area.replace(' Area', '');
          return area;
        });
      } else {
        areaTypeVal = measurmentsNotConsider.some(mea => mea === areaTypeVal) ? areaTypeVal : `${areaTypeVal}*`;
        areaTypeVal = areaTypeVal.replace(' Area', '');
      }
      const result =
        !!areaTypeVal && options.isPPTHasSpecificFormat ? [...areaTypeVal, 'Car Parks', 'Total(NLA)*'] : areaTypeVal;

      return result;
    },
  }, // Area Type
  264: {
    displayFn: (record: ReportRecord, options, _arg1, parkingCountArg) => {
      return getAreaSqmValues(record, options, true, parkingCountArg, true);
    },
  }, // Area Size
  98: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  }, // Primary Image
  99: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  }, // Additional Image
  254: {
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  }, // Secondary Image
  234: {
    displayFn: (record: ReportRecord, options, rent) => {
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          const uomArgument = { ...rent.uomArgument, isPrefix: true };
          rent = { ...rent, uomArgument };
          return mapAndFormatDynamicValue(record, rent, currencyLOV, true, true);
        })
      );
    },
    isVisible: (record: ReportRecord) => {
      const availableTypeVal = digger(record, 'data.ref_availability_type_desc');
      return availableTypeVal !== AVAILABILITY_TYPES.sale;
    },
  }, // Rent ($/sqm)
  537: { displayFn: displayFnWrapper }, // Stud height
  244: {
    displayFn: displayFnWrapper,
    isVisible: (record: ReportRecord) => {
      const availableTypeVal = digger(record, 'data.ref_availability_type_desc');
      return availableTypeVal !== AVAILABILITY_TYPES.lease;
    },
  }, // Land Area
  247: {
    displayFn: (record: ReportRecord, _, arg) => {
      let result = 'No';
      const sprinklered = mapAndFormatDynamicValue(record, arg);
      if (sprinklered && (sprinklered as string[]).some(a => a === 'Sprinklers')) {
        result = 'Yes';
      }
      return result;
    },
  }, // Sprinklered
  347: {
    // 347 - Floor Plan Images
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  },
  553: {
    displayFn: (record: ReportRecord, _, suite, floorNumber) => {
      const suiteVal = mapAndFormatDynamicValue(record, suite, null, null, false);
      const floorNumberVal = mapAndFormatDynamicValue(record, floorNumber, null, null, false);
      const floorPlanConst = ['Floorplan: ', 'Floor: ', ', Suite: '];
      const suitValArr: string[] = !Array.isArray(suiteVal) ? [suiteVal] : suiteVal;

      return (
        floorPlanConst[0] +
        suitValArr.map((val, i) => floorPlanConst[1] + concatValuesBy(floorPlanConst[2], floorNumberVal[i], val))
      );
    },
  }, // Floor/Suite
};
